<template>
  <div data-test-id="drag-and-drop-image-input">
    <DragAndDropFileInput
      ref="fileInput"
      preview-image
      accept=".jpg,.jpeg,.png"
      :max-file-size="maxFileSize"
      :file="file"
      :existing-image-url="existingImageUrl"
      title="Drag and drop your image file here"
      @file-selected="onFileSelected"
      @file-rejected="$emit('file-rejected', $event)"
      @clear-existing-image="$emit('clear-existing-image')"
    />
    <ImageCropper
      v-if="showCropper"
      :file-name="pendingFile.name"
      :image-url="pendingImageUrl"
      :aspect-ratio-value="aspectRatioValue"
      :aspect-ratio-text="aspectRatioText"
      :instruction-text="instructionText"
      @success="onCropSuccess"
      @close="onCropClose"
    />
    <v-text-field
      v-if="collectAltText && (file || existingImageUrl)"
      :value="altText"
      :rules="altTextRules"
      label="Image Alt Text"
      data-test-id="alt-text-input"
      outlined
      dense
      color="brandPrimary"
      hint="Read by screen readers for accessibility"
      persistent-hint
      class="mt-3"
      @input="$emit('update:altText', $event)"
    />
  </div>
</template>

<script>
import DragAndDropFileInput from "@/components/DragAndDropFileInput.vue";
import ImageCropper from "@/components/ImageCropper.vue";

const DEFAULT_ALT_TEXT = "image";

export default {
  name: "DragAndDropImageInput",
  components: {
    DragAndDropFileInput,
    ImageCropper,
  },
  props: {
    maxFileSize: {
      type: Number,
      default: null,
    },
    file: {
      type: File,
      default: null,
    },
    cropImage: {
      type: Boolean,
      default: false,
    },
    aspectRatioValue: {
      type: Number,
      default: undefined,
    },
    aspectRatioText: {
      type: String,
      default: undefined,
    },
    existingImageUrl: {
      type: String,
      default: null,
    },
    collectAltText: {
      type: Boolean,
      default: false,
    },
    altText: {
      type: String,
      default: "",
    },
    altTextRules: {
      type: Array,
      default: () => [],
    },
    instructionText: {
      type: String,
      default: null,
    },
  },
  emits: [
    "file-selected",
    "file-rejected",
    "clear-existing-image",
    "update:altText",
    "crop-ratio-applied",
  ],
  data() {
    return {
      showCropper: false,
      pendingFile: null,
      pendingImageUrl: null,
    };
  },
  methods: {
    // Seeds alt text so the accessibility field never starts blank; the user can still edit it.
    emitFileSelected(file) {
      if (file && this.collectAltText && !this.altText) {
        this.$emit("update:altText", DEFAULT_ALT_TEXT);
      }
      this.$emit("file-selected", file);
    },
    onFileSelected(file) {
      if (!file) {
        this.emitFileSelected(null);
        this.cleanupPending();
        return;
      }

      if (this.cropImage) {
        this.pendingFile = file;
        this.pendingImageUrl = URL.createObjectURL(file);
        this.showCropper = true;
      } else {
        this.emitFileSelected(file);
      }
    },
    onCropSuccess({ file }) {
      this.cleanupPending();
      // The cropper emits a brand-new File built from a canvas re-encode,
      // so its size is independent of the source file's size. Re-check
      // against maxFileSize here — the source-side check in
      // DragAndDropFileInput.handleFile only runs on the pre-crop file.
      const withinSizeLimit = this.$refs.fileInput?.validateFileSize(file) ?? true;
      if (!withinSizeLimit) {
        this.$emit("file-rejected", { reason: "size", file });
        this.$emit("crop-ratio-applied", false);
        return;
      }
      this.emitFileSelected(file);
      this.$emit("crop-ratio-applied", !!this.aspectRatioValue);
    },
    onCropClose() {
      this.emitFileSelected(null);
      this.$emit("crop-ratio-applied", false);
      this.cleanupPending();
    },
    cleanupPending() {
      if (this.pendingImageUrl) {
        URL.revokeObjectURL(this.pendingImageUrl);
      }
      this.pendingFile = null;
      this.pendingImageUrl = null;
      this.showCropper = false;
    },
  },
  beforeUnmount() {
    if (this.pendingImageUrl) {
      URL.revokeObjectURL(this.pendingImageUrl);
    }
  },
};
</script>
